<template>
  <UApp>
    <component :is="theLayout">
      <RouterView v-slot="{ Component }">
        <Suspense v-if="Component" :timeout="0">
          <template #default>
            <component :is="Component" />
          </template>
          <template #fallback>
            <div class="flex h-full w-full items-center justify-center">
              <LoadingSpinner />
            </div>
          </template>
        </Suspense>
      </RouterView>
    </component>
  </UApp>
</template>

<script setup>
import { computed, watch, onMounted, defineAsyncComponent } from 'vue';
import { useRoute } from 'vue-router';
import { useIdle } from '@vueuse/core';
import { useJwt } from '@vueuse/integrations/useJwt';
import { clearTokens } from '@/utils/auth';
import { getUserInitials } from '@/utils/user';
import { useUsersStore } from '@/stores/useUsers';
import { useMetrics } from '@/stores/useMetrics';
import { useWebSockets } from '@/stores/useWebSockets';
import router from '@/router';
import LoadingSpinner from '@/components/common/TheLoadingSpinner.vue';
import { useSkillBadgesStore } from './stores/useSkillBadges';

// Layouts
const DefaultLayout = defineAsyncComponent(
  () => import('@/layouts/DefaultLayout.vue')
);
const InfoLayout = defineAsyncComponent(
  () => import('@/layouts/InfoLayout.vue')
);

// Stores
const users_store = useUsersStore();
const skill_badges_store = useSkillBadgesStore();

// Constants
const route = useRoute();
const TIMEOUT = import.meta.env.VITE_AUTH_TIMEOUT || 30;
const { idle } = useIdle(TIMEOUT * 60 * 1000);
const NO_SIDEBAR_ROUTES = new Set([
  'login',
  'logout',
  'notfound',
  'forbidden',
  'unauthorized',
  'catchAll',
]);

const theLayout = computed(() => {
  if (!route.name || NO_SIDEBAR_ROUTES.has(route.name)) {
    return InfoLayout;
  }

  const hasValidToken = () => {
    const token = sessionStorage.getItem('id_token');
    return token && token !== 'undefined';
  };

  return hasValidToken() ? DefaultLayout : InfoLayout;
});

onMounted(async () => {
  await handlePageRefresh();
});

// Initial login
watch(route, async route => {
  if (route?.redirectedFrom?.path === '/status') {
    await handlePageRefresh();
    users_store.authActivity('login');
  }
});

watch(
  () => idle.value,
  () => {
    if (idle.value == true) {
      users_store.logOut();
    }
  }
);

async function handlePageRefresh() {
  const id_token = sessionStorage.getItem('id_token');
  if (!id_token || id_token == 'undefined') return;

  const { payload } = useJwt(id_token);

  //Pulling user id from token and giving it to the user store for UI processes
  users_store.activeUser = { auxo_user_id: payload.value.auxo_user_id };
  const user = await users_store.getUserByEmail(payload.value.email);

  if (!user) {
    clearTokens();
    return router.push('/login');
  }

  // Load user
  users_store.activeUser = {
    ...payload.value,
    initials: getUserInitials(user),
    username: user.username,
    auxo_user_id: user.auxo_user_id,
    photo_url: user.photo_url || 'none',
    is_active: user.is_active,
    daily_goal: user.user_goal || 0,
    shift_duration: user.shift_duration || { hours: 8, minutes: 30 },
    daily_login_time: user.daily_login_time || new Date().toISOString(),
  };

  // Load metrics
  useMetrics().setActiveUser(user.username, user.auxo_user_id);

  // Check for unseen badge notifications
  // Delayed to ensure that a log in event could be processed on the backend
  // Bit of a bandaid solution, we should consider a faster connection to the ws
  // or some sort of a queue system
  setTimeout(() => {
    skill_badges_store.getUnseenNotifications(
      users_store.activeUser.auxo_user_id
    );
  }, 2000);

  // All Users are loaded so the router guard can validate
  // a user exists by looking up their auxo_user_id
  if (!users_store.isLoading) {
    users_store.isLoading = true;
    await users_store.getAllUsers();
    users_store.isLoading = false;
  }

  if (!NO_SIDEBAR_ROUTES.has(route.name)) {
    try {
      useWebSockets().connect();
    } catch (error) {
      console.error('WebSocket connection failed:', error);
    }
  }
}
</script>
